/**
 * masterUrls.ts — canonical ALL-LOWERCASE master resume URLs.
 *
 * Browser mirror of `infrastructure/lambdas/shared/master_urls.py`. A master
 * (non-job-specific) resume URL is made to be SHARED — typed off a business
 * card, pasted into a message — so it must be case-insensitive. The objects
 * are published all-lowercase and the CloudFront rewriter lowercases the
 * incoming URI, so every link the app shows must be lowercase too or we hand
 * the user a URL that differs from the one they'll get back.
 *
 * JOB-specific URLs (`/company/role/...`) are deliberately NOT covered — they
 * are per-application links and keep their legacy mixed-case artifact names.
 */

/** Canonical lowercase filename of the PDF published beside a master page. */
export const MASTER_PDF_FILENAME = 'resume.pdf';

/** The stored / published form of a master resume path segment. */
export function normalizeMasterPath(path: string | null | undefined): string {
  if (!path || typeof path !== 'string') return '';
  return path.trim().replace(/^\/+|\/+$/g, '').trim().toLowerCase();
}

/** The shareable https URL for a master resume page (never trailing-slashed). */
export function masterResumeUrl(
  username: string | null | undefined,
  /** Omitted (or blank) = the account ROOT, which is the generic resume URL. */
  path?: string | null,
): string {
  const user = (username || '').trim().toLowerCase();
  const seg = normalizeMasterPath(path);
  const base = `https://${user}.skillsnap.me`;
  return seg ? `${base}/${seg}` : base;
}

/** The PDF published beside a master resume page. */
export function masterPdfUrl(url: string): string {
  return `${(url || '').replace(/\/+$/, '')}/${MASTER_PDF_FILENAME}`;
}
